import React, { useState, useEffect, useRef } from 'react';
import { memberLookup, memberVerify } from '../api';
import './MembershipModal.css';

const RESEND_COOLDOWN = 60;

const MembershipModal = ({ venueId, onClose, onVerified }) => {
  const [step, setStep] = useState(1); // 1 = contact entry, 2 = OTP
  const [contact, setContact] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const timerRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-focus input when step changes
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, [step]);

  // Resend cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    timerRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [resendCooldown]);

  // Close on backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleSendCode = async () => {
    if (!contact.trim() || loading) return;
    setLoading(true);
    setError(null);
    try {
      await memberLookup(venueId, contact.trim());
      setStep(2);
      setResendCooldown(RESEND_COOLDOWN);
      setOtp('');
    } catch (err) {
      setError(err.message || 'No membership found for this contact.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (otp.length < 6 || loading) return;
    setLoading(true);
    setError(null);
    try {
      const data = await memberVerify(venueId, contact.trim(), otp.trim());
      onVerified(data);
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.');
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || loading) return;
    setLoading(true);
    setError(null);
    try {
      await memberLookup(venueId, contact.trim());
      setResendCooldown(RESEND_COOLDOWN);
      setOtp('');
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to resend code.');
    } finally {
      setLoading(false);
    }
  };

  const handleContactKeyDown = (e) => {
    if (e.key === 'Enter') handleSendCode();
  };

  const handleOtpKeyDown = (e) => {
    if (e.key === 'Enter') handleVerify();
  };

  return (
    <div className="membership-backdrop" onClick={handleBackdropClick} role="dialog" aria-modal="true" aria-label="Membership verification">
      <div className="membership-sheet">
        {/* Sheet handle */}
        <div className="membership-handle" aria-hidden="true" />

        {/* Close button */}
        <button className="membership-close-btn" onClick={onClose} aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {step === 1 ? (
          <>
            <div className="membership-icon" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <h3 className="membership-title">Member Login</h3>
            <p className="membership-subtitle">
              Enter your email or phone number to apply your membership discount.
            </p>

            <div className="membership-field">
              <label className="membership-label" htmlFor="membership-contact">Email or phone</label>
              <input
                ref={inputRef}
                id="membership-contact"
                className="membership-input"
                type="text"
                value={contact}
                onChange={e => setContact(e.target.value)}
                onKeyDown={handleContactKeyDown}
                placeholder="e.g. john@example.com or 07700 900000"
                autoComplete="email"
                inputMode="email"
                disabled={loading}
              />
            </div>

            {error && <p className="membership-error" role="alert">{error}</p>}

            <button
              className="membership-primary-btn"
              onClick={handleSendCode}
              disabled={!contact.trim() || loading}
            >
              {loading ? <span className="membership-spinner" aria-hidden="true" /> : 'Send Code'}
            </button>
          </>
        ) : (
          <>
            <div className="membership-icon membership-icon--otp" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                <line x1="12" y1="18" x2="12.01" y2="18" />
              </svg>
            </div>
            <h3 className="membership-title">Enter Code</h3>
            <p className="membership-subtitle">
              Enter the 6-digit code sent to <strong>{contact}</strong>
            </p>

            <div className="membership-field">
              <label className="membership-label" htmlFor="membership-otp">6-digit code</label>
              <input
                ref={inputRef}
                id="membership-otp"
                className="membership-input membership-input--otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={handleOtpKeyDown}
                placeholder="000000"
                autoComplete="one-time-code"
                disabled={loading}
              />
            </div>

            {error && <p className="membership-error" role="alert">{error}</p>}

            <button
              className="membership-primary-btn"
              onClick={handleVerify}
              disabled={otp.length < 6 || loading}
            >
              {loading ? <span className="membership-spinner" aria-hidden="true" /> : 'Verify'}
            </button>

            <div className="membership-resend-row">
              {resendCooldown > 0 ? (
                <span className="membership-resend-timer">Resend code in {resendCooldown}s</span>
              ) : (
                <button
                  className="membership-resend-btn"
                  onClick={handleResend}
                  disabled={loading}
                >
                  Resend code
                </button>
              )}
              <button
                className="membership-change-contact-btn"
                onClick={() => { setStep(1); setError(null); setOtp(''); }}
                disabled={loading}
              >
                Change contact
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MembershipModal;
